import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable, combineLatest, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { TableColumn, PaginationConfig, FilterChip } from '@shared/components/data-table/data-table.component';
import { TableSurfaceComponent } from '@shared/components/table-surface/table-surface.component';
import { AutocompleteComponent } from '@shared/components/autocomplete/autocomplete.component';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { OrganizationUnitDto } from '@shared/services/organization-unit.service';
import { OrganizationUnitByLevelDto } from '@core/api/services/organization-unit-api.service';
import { UnitService } from '../services/unit.service';
import { OrganizationLevel, ApostolicAreaDto, DistrictDto, CommunityGroupDto } from '../models/unit.model';
import { DeleteConfirmDialogComponent, DeleteConfirmDialogData } from '@features/user/components/shared/delete-confirm-dialog/delete-confirm-dialog.component';
import { NotificationService } from '@shared/services/notification.service';
import { UserFullInfoService } from '@core/services/user-full-info.service';

@Component({
  selector: 'app-unit-list-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatCardModule,
    TableSurfaceComponent,
    AutocompleteComponent,
  ],
  templateUrl: './unit-list-page.component.html',
  styleUrls: ['./unit-list-page.component.scss']
})
export class UnitListPageComponent implements OnInit, OnDestroy {
  columns: TableColumn[] = [
    { key: 'name', header: 'Nombre', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'group', header: 'Grupo', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'district', header: 'Distrito', sortable: true, hideable: true, visible: true, resizable: true },
    { key: 'area', header: 'Área', sortable: true, hideable: true, visible: true, resizable: true }
  ];

  activeFilterChips: FilterChip[] = [];
  tableData: OrganizationUnitDto[] = [];
  isTableLoading = false;
  tableErrorMessage = '';

  readonly sortBy$ = new BehaviorSubject<string>('name');
  readonly sortDirection$ = new BehaviorSubject<'asc' | 'desc'>('asc');

  get sortBy(): string { return this.sortBy$.value; }
  get sortDirection(): 'asc' | 'desc' { return this.sortDirection$.value; }

  areas$!: Observable<ApostolicAreaDto[]>;
  districts$!: Observable<DistrictDto[]>;
  communityGroups$!: Observable<CommunityGroupDto[]>;
  filteredDistricts$!: Observable<DistrictDto[]>;
  filteredCommunityGroups$!: Observable<CommunityGroupDto[]>;

  loading$ = new BehaviorSubject<boolean>(true);
  error$ = new BehaviorSubject<string | null>(null);
  private allUnitsById: Map<string, OrganizationUnitDto> = new Map();

  filterForm!: FormGroup;

  readonly pageSize$ = new BehaviorSubject<number>(10);
  readonly currentPage$ = new BehaviorSubject<number>(1);
  filteredCount = 0;
  totalCount = 0;
  canManageCommunityData = false;

  private destroy$ = new Subject<void>();

  constructor(
    private unitService: UnitService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private userFullInfoService: UserFullInfoService
  ) {
    this.filterForm = this.fb.group({
      searchName: [''],
      selectedArea: [''],
      selectedDistrict: [''],
      selectedGroup: ['']
    });
  }

  ngOnInit(): void {
    this.userFullInfoService.getUserInfo$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.canManageCommunityData = this.userFullInfoService.canManageMemberDataSnapshot(user);
        this.cdr.markForCheck();
      });

    const higherLevelUnits$ = this.unitService.getHigherLevelUnits(OrganizationLevel.Comunidad).pipe(shareReplay(1));

    this.areas$ = higherLevelUnits$.pipe(
      map((groupedByLevel: OrganizationUnitByLevelDto[]) => {
        const areaLevel = groupedByLevel.find(g => g.organizationLevelId === OrganizationLevel.AreaApostol);
        return (areaLevel?.units || [])
          .map(u => ({ id: u.id, code: '', name: u.name } as ApostolicAreaDto))
          .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
      })
    );

    this.districts$ = higherLevelUnits$.pipe(
      map((groupedByLevel: OrganizationUnitByLevelDto[]) => {
        const districtLevel = groupedByLevel.find(g => g.organizationLevelId === OrganizationLevel.Distrito);
        return (districtLevel?.units || [])
          .map(u => ({ id: u.id, code: '', name: u.name, apostolicAreaId: u.parentUnitId || '' } as DistrictDto))
          .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
      })
    );

    this.communityGroups$ = higherLevelUnits$.pipe(
      map((groupedByLevel: OrganizationUnitByLevelDto[]) => {
        const groupLevel = groupedByLevel.find(g => g.organizationLevelId === OrganizationLevel.GrupoComunidad);
        return (groupLevel?.units || [])
          .map(u => ({ id: u.id, code: '', name: u.name, districtId: u.parentUnitId || '' } as CommunityGroupDto))
          .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
      })
    );

    higherLevelUnits$.pipe(takeUntil(this.destroy$)).subscribe((groupedByLevel: OrganizationUnitByLevelDto[]) => {
      this.allUnitsById.clear();
      groupedByLevel.forEach(group => {
        (group.units ?? []).forEach(unit => {
          if (unit.id) this.allUnitsById.set(unit.id, unit as unknown as OrganizationUnitDto);
        });
      });
    });

    const selectedArea$ = this.filterForm.get('selectedArea')!.valueChanges.pipe(startWith(this.filterForm.value.selectedArea));
    const selectedDistrict$ = this.filterForm.get('selectedDistrict')!.valueChanges.pipe(startWith(this.filterForm.value.selectedDistrict));
    const selectedGroup$ = this.filterForm.get('selectedGroup')!.valueChanges.pipe(startWith(this.filterForm.value.selectedGroup));

    this.filteredDistricts$ = combineLatest([this.districts$, selectedArea$]).pipe(
      map(([districts, areaId]) =>
        (areaId ? districts.filter(d => d.apostolicAreaId === areaId) : districts)
          .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }))
      ),
      shareReplay(1)
    );

    this.filteredCommunityGroups$ = combineLatest([this.communityGroups$, this.districts$, selectedArea$, selectedDistrict$]).pipe(
      map(([groups, districts, areaId, districtId]) => {
        let result: CommunityGroupDto[];
        if (districtId) {
          result = groups.filter(g => g.districtId === districtId);
        } else if (areaId) {
          const districtIds = districts.filter(d => d.apostolicAreaId === areaId).map(d => d.id);
          result = groups.filter(g => districtIds.includes(g.districtId));
        } else {
          result = groups;
        }
        return result.sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
      }),
      shareReplay(1)
    );

    const filterValue$ = this.filterForm.valueChanges.pipe(
      startWith(this.filterForm.value),
      debounceTime(200),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
    );

    const paginatedUnits$ = combineLatest([
      filterValue$,
      this.pageSize$,
      this.currentPage$,
      this.sortBy$,
      this.sortDirection$
    ]).pipe(
      tap(() => {
        this.isTableLoading = true;
        this.cdr.markForCheck();
      }),
      switchMap(([filters, pageSize, currentPage, sortBy, sortDirection]) =>
        this.unitService.getCommunities(
          currentPage,
          pageSize,
          filters.searchName || undefined,
          sortBy,
          sortDirection,
          filters.selectedArea || undefined,
          filters.selectedDistrict || undefined,
          filters.selectedGroup || undefined
        ).pipe(
          map(response => ({
            items: response.items || [],
            totalCount: response.totalCount || 0,
            totalPages: response.totalPages || 0
          }))
        )
      ),
      shareReplay(1)
    );

    paginatedUnits$.pipe(takeUntil(this.destroy$)).subscribe(result => {
      this.tableData = result.items;
      this.filteredCount = result.totalCount;
      this.totalCount = result.totalCount;
      this.isTableLoading = false;
      this.tableErrorMessage = '';
      this.buildFilterChips();
      this.cdr.markForCheck();
    });

    higherLevelUnits$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.loading$.next(false);
        this.error$.next(null);
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.loading$.next(false);
        this.error$.next(err?.message || 'Error desconocido');
        this.cdr.detectChanges();
      }
    });

    selectedArea$.pipe(takeUntil(this.destroy$)).subscribe(areaId => {
      const districtControl = this.filterForm.get('selectedDistrict')!;
      const groupControl = this.filterForm.get('selectedGroup')!;
      if (!areaId) {
        districtControl.enable({ emitEvent: false });
        groupControl.enable({ emitEvent: false });
        return;
      }
      const currentDistrictId = districtControl.value;
      if (currentDistrictId) {
        const district = this.allUnitsById.get(currentDistrictId);
        if (!district || district.organizationLevelId !== OrganizationLevel.Distrito || district.parentUnitId !== areaId) {
          districtControl.setValue('', { emitEvent: true });
          groupControl.setValue('', { emitEvent: true });
        }
      }
      districtControl.enable({ emitEvent: false });
      groupControl.enable({ emitEvent: false });
    });

    selectedDistrict$.pipe(takeUntil(this.destroy$)).subscribe(districtId => {
      const areaControl = this.filterForm.get('selectedArea')!;
      const groupControl = this.filterForm.get('selectedGroup')!;
      if (!districtId) {
        areaControl.enable({ emitEvent: false });
        groupControl.enable({ emitEvent: false });
        return;
      }
      const districtUnit = this.allUnitsById.get(districtId);
      if (districtUnit && districtUnit.organizationLevelId === OrganizationLevel.Distrito) {
        areaControl.setValue(districtUnit.parentUnitId || '', { emitEvent: true });
        areaControl.disable({ emitEvent: false });
      }
      const currentGroupId = groupControl.value;
      if (currentGroupId) {
        const groupUnit = this.allUnitsById.get(currentGroupId);
        if (!groupUnit || groupUnit.organizationLevelId !== OrganizationLevel.GrupoComunidad || groupUnit.parentUnitId !== districtId) {
          groupControl.setValue('', { emitEvent: true });
        }
      }
      groupControl.enable({ emitEvent: false });
    });

    selectedGroup$.pipe(takeUntil(this.destroy$)).subscribe(groupId => {
      const areaControl = this.filterForm.get('selectedArea')!;
      const districtControl = this.filterForm.get('selectedDistrict')!;
      if (!groupId) {
        areaControl.enable({ emitEvent: false });
        districtControl.enable({ emitEvent: false });
        return;
      }
      const group = this.allUnitsById.get(groupId);
      if (!group || group.organizationLevelId !== OrganizationLevel.GrupoComunidad) return;
      const parentDistrictId = group.parentUnitId || '';
      districtControl.setValue(parentDistrictId, { emitEvent: true });
      districtControl.disable({ emitEvent: false });
      const districtUnit = this.allUnitsById.get(parentDistrictId);
      if (districtUnit) {
        areaControl.setValue(districtUnit.parentUnitId || '', { emitEvent: true });
        areaControl.disable({ emitEvent: false });
      }
    });
  }

  get paginationConfig(): PaginationConfig {
    return {
      currentPage: this.currentPage$.value,
      pageSize: this.pageSize$.value,
      totalCount: this.filteredCount,
      totalPages: Math.ceil(this.filteredCount / this.pageSize$.value)
    };
  }

  onPageChange(event: { page: number }): void {
    this.currentPage$.next(event.page);
  }

  onPageSizeChange(event: { pageSize: number }): void {
    this.pageSize$.next(event.pageSize);
    this.currentPage$.next(1);
  }

  onSortChange(columnKey: string): void {
    const columnSortMap: Record<string, string> = {
      name: 'name',
      group: 'group',
      district: 'district',
      area: 'area'
    };
    const sortField = columnSortMap[columnKey];
    if (!sortField) return;
    if (this.sortBy === sortField) {
      this.sortDirection$.next(this.sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortBy$.next(sortField);
      this.sortDirection$.next('asc');
    }
    this.currentPage$.next(1);
    this.cdr.markForCheck();
  }

  onAreaChanged(areaId: string | null): void {
    this.filterForm.patchValue({ selectedArea: areaId || '' }, { emitEvent: true });
  }

  onDistrictChanged(districtId: string | null): void {
    this.filterForm.patchValue({ selectedDistrict: districtId || '' }, { emitEvent: true });
  }

  onGroupChanged(groupId: string | null): void {
    this.filterForm.patchValue({ selectedGroup: groupId || '' }, { emitEvent: true });
  }

  private buildFilterChips(): void {
    const v = this.filterForm.value;
    const chips: FilterChip[] = [];
    if (v.searchName) chips.push({ key: 'searchName', label: `Buscar: "${v.searchName}"`, removable: true });
    if (v.selectedArea) {
      const area = this.allUnitsById.get(v.selectedArea);
      chips.push({ key: 'selectedArea', label: `Área: ${area?.name || v.selectedArea}`, removable: true });
    }
    if (v.selectedDistrict) {
      const district = this.allUnitsById.get(v.selectedDistrict);
      chips.push({ key: 'selectedDistrict', label: `Distrito: ${district?.name || v.selectedDistrict}`, removable: true });
    }
    if (v.selectedGroup) {
      const group = this.allUnitsById.get(v.selectedGroup);
      chips.push({ key: 'selectedGroup', label: `Grupo: ${group?.name || v.selectedGroup}`, removable: true });
    }
    this.activeFilterChips = chips;
    this.cdr.markForCheck();
  }

  onToolbarSearch(term: string): void {
    this.filterForm.get('searchName')!.setValue(term);
  }

  onFilterRemove(key: string): void {
    this.filterForm.get(key)?.setValue('');
  }

  createNew(): void {
    if (!this.canManageCommunityData) {
      return;
    }

    this.router.navigate(['/units/new']);
  }

  getAreaName(unit: OrganizationUnitDto): string {
    const group = this.allUnitsById.get(unit.parentUnitId || '');
    const district = group ? this.allUnitsById.get(group.parentUnitId || '') : undefined;
    const area = district ? this.allUnitsById.get(district.parentUnitId || '') : undefined;
    return area?.name || '-';
  }

  getAreaId(unit: OrganizationUnitDto): string | null {
    const group = this.allUnitsById.get(unit.parentUnitId || '');
    const district = group ? this.allUnitsById.get(group.parentUnitId || '') : undefined;
    const area = district ? this.allUnitsById.get(district.parentUnitId || '') : undefined;
    return area?.id || null;
  }

  getDistrictName(unit: OrganizationUnitDto): string {
    const group = this.allUnitsById.get(unit.parentUnitId || '');
    const district = group ? this.allUnitsById.get(group.parentUnitId || '') : undefined;
    return district?.name || '-';
  }

  getDistrictId(unit: OrganizationUnitDto): string | null {
    const group = this.allUnitsById.get(unit.parentUnitId || '');
    const district = group ? this.allUnitsById.get(group.parentUnitId || '') : undefined;
    return district?.id || null;
  }

  getGroupName(unit: OrganizationUnitDto): string {
    const group = this.allUnitsById.get(unit.parentUnitId || '');
    return group?.name || '-';
  }

  getGroupId(unit: OrganizationUnitDto): string | null {
    return this.allUnitsById.get(unit.parentUnitId || '')?.id || null;
  }

  navigateToUnit(unitId: string): void {
    this.router.navigate(['/units', unitId]);
  }

  openDeleteDialog(unit: OrganizationUnitDto): void {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, modalDialogConfig({
      data: {
        title: 'Eliminar comunidad',
        message: `¿Estás seguro de que querés eliminar <strong>${unit.name}</strong>? Esta acción no se puede deshacer.`,
        confirmButtonText: 'Eliminar',
        requiresReason: true,
      } as DeleteConfirmDialogData,
      size: 'sm',
      layout: 'confirm',
      tone: 'danger',
    }));

    dialogRef.afterClosed().pipe(
      filter(result => result?.confirmed === true),
      takeUntil(this.destroy$)
    ).subscribe(result => {
      const reason: string = result.deletionReason;
      this.unitService.deleteUnit(unit.id!, reason).subscribe({
        next: () => {
          this.notificationService.success(`La comunidad "${unit.name}" fue eliminada correctamente.`);
          this.currentPage$.next(this.currentPage$.value);
        },
        error: (err) => {
          const message = err?.detail || err?.message || 'No se pudo eliminar la unidad.';
          this.notificationService.error(message);
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
